import { useEffect, useRef } from 'react';
import logger from '@ayocore/platform/common/helpers/logger';
import { useTab } from '@ayocore/platform/client/tabs';
import { useCommentRepository } from '@ayocore/comments/client/rpc';
import { useTaskRepository, useTaskStatusRepository } from '@ayocore/tasks/client/rpc';
import {
  useActivityEventRepository,
  useActivityPinRepository,
  useActivityThreadRepository,
} from '@ayocore/activity/client/rpc';
import { crmActivityChannel } from '@ayocore/activity/client/messaging';
import type { ActivityChannelMessage } from '@ayocore/activity/common/types/activity-message';
import { ActivityController } from '../controllers/ActivityController';
import { useControllerSelector } from './useControllerSelector';

const TRANSIENT_SIGNAL_TTL_MS = 60_000;
const TRANSIENT_SIGNAL_MAX_KEYS = 1_000;

function cleanupTransientSignalKeys(store: Map<string, number>, now: number): void {
  store.forEach((timestamp, key) => {
    if (now - timestamp > TRANSIENT_SIGNAL_TTL_MS) {
      store.delete(key);
    }
  });

  while (store.size > TRANSIENT_SIGNAL_MAX_KEYS) {
    const oldestKey = store.keys().next().value;
    if (!oldestKey) break;
    store.delete(oldestKey);
  }
}

/**
 * Lifecycle-хук root-контроллера Activity.
 * Отвечает за инициализацию контроллера, realtime-подписки и auto mark-as-read.
 */
export function useActivityRoot(options?: { isModal?: boolean }): ActivityController {
  const { setTabTitle } = useTab();
  const threadRepository = useActivityThreadRepository();
  const eventRepository = useActivityEventRepository();
  const pinRepository = useActivityPinRepository();
  const commentRepository = useCommentRepository();
  const taskRepository = useTaskRepository();
  const taskStatusRepository = useTaskStatusRepository();

  const controllerRef = useRef<ActivityController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new ActivityController({
      threadRepository,
      eventRepository,
      pinRepository,
      commentRepository,
      taskRepository,
      taskStatusRepository,
    });
  }
  const controller = controllerRef.current;

  const selectedThread = useControllerSelector(controller, state => state.selectedThread);
  const markReadInFlightRef = useRef<Set<number>>(new Set());
  const lastSeenEventIdByThreadRef = useRef<Map<number, number>>(new Map());
  const transientSignalKeysRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    controller.updateRepositories({
      threadRepository,
      eventRepository,
      pinRepository,
      commentRepository,
      taskRepository,
      taskStatusRepository,
    });
  }, [
    controller,
    threadRepository,
    eventRepository,
    pinRepository,
    commentRepository,
    taskRepository,
    taskStatusRepository,
  ]);

  useEffect(() => {
    if (!options?.isModal) {
      setTabTitle('Сообщения');
    }
  }, [setTabTitle, options?.isModal]);

  useEffect(() => {
    void controller.loadThreads();

    return () => {
      controller.dispose();
    };
  }, [controller]);

  useEffect(() => {
    const unsubscribeConnectionStart = crmActivityChannel.onConnectionStart(() => {
      logger.debug('[Activity:Root] connection restored, force resync');
      lastSeenEventIdByThreadRef.current.clear();
      transientSignalKeysRef.current.clear();
      controller.forceResync();
    });

    const unsubscribeConnectionLost = crmActivityChannel.onConnectionLost(() => {
      logger.debug('[Activity:Root] connection lost');
    });

    const unsubscribeConnectionError = crmActivityChannel.onError(error => {
      logger.warn('[Activity:Root] channel error', { error });
    });

    const unsubscribeMessage = crmActivityChannel.onMessage(message => {
      if (!isValidRealtimeThreadMessage(message)) return;

      if (
        isRealtimeMessageDuplicated(
          message,
          lastSeenEventIdByThreadRef.current,
          transientSignalKeysRef.current
        )
      ) {
        return;
      }
      controller.handleRealtimeMessage(message);
    });

    return () => {
      unsubscribeMessage();
      unsubscribeConnectionStart();
      unsubscribeConnectionLost();
      unsubscribeConnectionError();
    };
  }, [controller]);

  useEffect(() => {
    if (!selectedThread) return;

    const attentionUnreadCount = Number(selectedThread.attention_unread_count ?? 0);
    if (selectedThread.unread_count <= 0 && attentionUnreadCount <= 0) return;

    const threadId = selectedThread.id;
    if (markReadInFlightRef.current.has(threadId)) return;

    markReadInFlightRef.current.add(threadId);
    void controller
      .markThreadRead(threadId)
      .then(() => controller.loadThreads({ showLoader: false }))
      .finally(() => {
        markReadInFlightRef.current.delete(threadId);
      });
  }, [
    controller,
    selectedThread?.id,
    selectedThread?.unread_count,
    selectedThread?.attention_unread_count,
  ]);

  return controller;
}

function isValidRealtimeThreadMessage(message: ActivityChannelMessage): boolean {
  return typeof message.thread_id === 'number' && message.thread_id > 0;
}

function isRealtimeMessageDuplicated(
  message: ActivityChannelMessage,
  lastSeenEventIdByThread: Map<number, number>,
  transientSignalKeys: Map<string, number>
): boolean {
  const threadId = message.thread_id;
  if (message.event_id != null && message.event_id > 0) {
    const lastSeen = lastSeenEventIdByThread.get(threadId) ?? 0;
    if (message.event_id <= lastSeen) return true;
    lastSeenEventIdByThread.set(threadId, message.event_id);
    return false;
  }

  const dedupeKey = [
    threadId,
    message.event_type,
    message.reason ?? '',
    message.comment_uuid ?? '',
    message.occurred_at,
  ].join(':');

  if (transientSignalKeys.has(dedupeKey)) return true;

  const now = Date.now();
  transientSignalKeys.set(dedupeKey, now);
  cleanupTransientSignalKeys(transientSignalKeys, now);
  return false;
}
